import {useEffect, useMemo, useRef, useState} from 'react';
import Axios from 'axios';

import type {SearchAllResult} from '@deezer/next/services/api/Starmania/requests/search/types';
import StarmaniaApi from '@deezer/next/services/api/Starmania';

import {uniqid} from '@deezer/next/shared/utils/id';
import SearchMode from '@deezer/next/shared/utils/SearchMode';

import {useDebouncer} from '@deezer/next/shared/hooks/useDebouncer';

type SearchFunction = {
	(searchQuery: string, params?: object): Promise<void> | undefined;

	cancel: () => void;
	flush: () => Promise<void> | undefined;
};

export const useGlobalSearch = <T extends SearchMode>(searchMode: T) => {
	const [isSearching, setIsSearching] = useState(false);

	const [results, setResults] = useState<SearchAllResult>();

	const [error, setError] = useState<any>();

	const requestIdsRef = useRef<string[]>([]);

	const {schedule, cancel, flush} = useDebouncer<Promise<void>>(200);

	const search = useMemo<SearchFunction>(() => {
		const debouncedSearch = (
			searchQuery: string,
			params?: object,
		): Promise<void> | undefined =>
			schedule(async () => {
				const previousRequestIds = [...requestIdsRef.current];

				const newRequestId = uniqid();

				requestIdsRef.current.push(newRequestId);

				setIsSearching(true);

				try {
					previousRequestIds.forEach(requestId => {
						StarmaniaApi.cancel(requestId);
					});

					const response = await StarmaniaApi.search.searchAll(
						searchQuery,
						searchMode === SearchMode.editArtist ? 'artist' : '',
						newRequestId,
						params,
					);

					setResults(response);
					setError(undefined);
					setIsSearching(false);
				} catch (error) {
					if (!Axios.isCancel(error)) {
						setResults(undefined);
						setError(error);

						// Only reset isSearching if an error different
						// from a cancellation happens.
						setIsSearching(false);
					}
				} finally {
					// Clear out the current request id no matter if
					// it's successful or cancelled.
					requestIdsRef.current = requestIdsRef.current.filter(
						requestId => requestId !== newRequestId,
					);
				}
			});

		debouncedSearch.cancel = cancel;
		debouncedSearch.flush = flush;

		return debouncedSearch;
	}, [cancel, flush, schedule, searchMode]);

	useEffect(() => {
		return () => {
			search.cancel();
		};
	}, [search]);

	return {
		isSearching,
		results,
		error,
		setResults,
		search,
	};
};

export default useGlobalSearch;
